import { useState, useRef, useEffect } from "react";

const API_URL     = "https://bpkz5p7qh4.execute-api.us-east-1.amazonaws.com/prod/query";
const STORAGE_KEY = "sr_visitor";

function getOrCreateVisitorId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY, id);
      document.cookie = `sr_vid=${id};max-age=${60*60*24*365};path=/;SameSite=Lax`;
    }
    return id;
  } catch { return crypto.randomUUID(); }
}

async function getFingerprint() {
  try {
    const raw = [
      navigator.userAgent, navigator.language,
      window.screen.width+"x"+window.screen.height,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.platform,
    ].join("|");
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("").slice(0,16);
  } catch { return "unknown"; }
}

const EXAMPLES = [
  "What penalties did PURA impose on Eversource after Isaias?",
  "What is Connecticut's PBR framework and which docket established it?",
  "What were the union concerns in the Avangrid merger proceeding?",
  "What storm costs did UI claim in the Isaias proceeding?",
];

const Dots = () => (
  <span style={{display:"inline-flex",gap:4,alignItems:"center",padding:"4px 0"}}>
    {[0,1,2].map(i=>(
      <span key={i} style={{
        width:6,height:6,borderRadius:"50%",background:"#64748b",
        animation:"pulse 1.4s ease-in-out infinite",animationDelay:`${i*0.2}s`,
      }}/>
    ))}
  </span>
);

const Citation = ({s,i}) => (
  <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 12px",
    background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,fontSize:12,color:"#475569"}}>
    <span style={{minWidth:20,height:20,background:"#0f172a",color:"#f8fafc",borderRadius:4,
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:10,fontWeight:700,fontFamily:"monospace",flexShrink:0}}>{i+1}</span>
    <div style={{flex:1,minWidth:0}}>
      <div style={{fontWeight:600,color:"#0f172a",marginBottom:2}}>{s.document}</div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <span>Docket {s.docket}</span>
        {s.filer&&<span>· {s.filer}</span>}
        {s.date&&<span>· {s.date}</span>}
        <span style={{marginLeft:"auto",background:"#dbeafe",color:"#1e40af",
          padding:"1px 6px",borderRadius:4,fontWeight:600}}>{Math.round(s.score*100)}%</span>
      </div>
    </div>
  </div>
);

const Msg = ({msg}) => {
  const u = msg.role==="user";
  return (
    <div style={{display:"flex",justifyContent:u?"flex-end":"flex-start",
      marginBottom:24,gap:12,alignItems:"flex-start"}}>
      {!u&&(
        <div style={{width:32,height:32,borderRadius:8,background:"#0f172a",
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
          <span style={{color:"#f8fafc",fontSize:12,fontWeight:700,fontFamily:"monospace"}}>SR</span>
        </div>
      )}
      <div style={{maxWidth:"80%",minWidth:0}}>
        <div style={{
          background:u?"#0f172a":"#ffffff", color:u?"#f8fafc":"#0f172a",
          padding:"12px 16px",
          borderRadius:u?"16px 16px 4px 16px":"4px 16px 16px 16px",
          border:u?"none":"1px solid #e2e8f0",
          fontSize:14,lineHeight:1.7,whiteSpace:"pre-wrap",
        }}>
          {msg.loading?<Dots/>:msg.content}
        </div>
        {msg.sources&&msg.sources.length>0&&(
          <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4}}>
            <div style={{fontSize:11,fontWeight:600,color:"#94a3b8",
              textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Sources</div>
            {msg.sources.map((s,i)=><Citation key={i} s={s} i={i}/>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default function App() {
  const [vid]          = useState(()=>getOrCreateVisitorId());
  const [msgs, setMsgs]= useState([]);
  const [input, setInput]= useState("");
  const [loading, setLoading]= useState(false);
  const [remaining, setRemaining]= useState(5);
  const [limited, setLimited]= useState(false);
  const [showEmail, setShowEmail]= useState(false);
  const [email, setEmail]= useState("");
  const [emailDone, setEmailDone]= useState(false);
  const bot = useRef(null);
  const inp = useRef(null);

  useEffect(()=>{bot.current?.scrollIntoView({behavior:"smooth"});},[msgs]);

  function history() {
    return msgs.filter(m=>!m.loading&&m.content).map(m=>({role:m.role,content:m.content}));
  }

  async function send(q) {
    if (!q.trim()||loading||limited) return;
    setMsgs(p=>[...p,{role:"user",content:q},{role:"assistant",content:"",loading:true}]);
    setInput("");
    setLoading(true);
    try {
      const fp  = await getFingerprint();
      const res = await fetch(API_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({question:q,visitor_id:vid,fingerprint_hash:fp,history:history()}),
      });
      if (res.status===429) {
        setMsgs(p=>p.map((m,i)=>i===p.length-1
          ?{role:"assistant",content:"You've reached your free query limit for today. Enter your email below to continue.",sources:[]}:m));
        setLimited(true); setRemaining(0);
        setTimeout(()=>setShowEmail(true),600);
        return;
      }
      const d = await res.json();
      setMsgs(p=>p.map((m,i)=>i===p.length-1
        ?{role:"assistant",content:d.answer||"Something went wrong.",sources:d.sources||[]}:m));
      const r = d.queries_remaining??remaining-1;
      setRemaining(r);
      if (r<=0&&!emailDone) setTimeout(()=>setShowEmail(true),800);
    } catch {
      setMsgs(p=>p.map((m,i)=>i===p.length-1
        ?{role:"assistant",content:"Connection error. Please try again.",sources:[]}:m));
    } finally {
      setLoading(false);
      setTimeout(()=>inp.current?.focus(),100);
    }
  }

  async function submitEmail(e) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await fetch(API_URL,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({visitor_id:vid,email})});
    } catch {}
    setEmailDone(true); setShowEmail(false); setLimited(false); setRemaining(999);
  }

  const empty = msgs.length===0;

  return (
    <div style={{height:"100vh",display:"flex",flexDirection:"column",
      background:"#f8fafc",fontFamily:"'Georgia','Times New Roman',serif"}}>
      <style>{`
        @keyframes pulse{0%,80%,100%{opacity:.3;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        textarea:focus{outline:none} textarea{resize:none} *{box-sizing:border-box}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:2px}
      `}</style>

      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"14px 24px",
        display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:32,height:32,borderRadius:8,background:"#0f172a",
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{color:"#f8fafc",fontSize:12,fontWeight:700,fontFamily:"monospace"}}>SR</span>
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:"#0f172a",letterSpacing:"-0.01em"}}>SessionResearch</div>
            <div style={{fontSize:11,color:"#94a3b8",letterSpacing:"0.02em"}}>Connecticut PURA Intelligence</div>
          </div>
        </div>
        {emailDone?(
          <div style={{fontSize:12,color:"#16a34a",background:"#f0fdf4",
            padding:"4px 10px",borderRadius:6,border:"1px solid #bbf7d0"}}>✓ Unlimited access</div>
        ):(
          <div style={{fontSize:12,fontFamily:"monospace",
            color:remaining<=1?"#dc2626":"#64748b",
            background:remaining<=1?"#fef2f2":"#f1f5f9",
            padding:"4px 10px",borderRadius:6,
            border:`1px solid ${remaining<=1?"#fecaca":"#e2e8f0"}`}}>
            {remaining} free {remaining===1?"query":"queries"} remaining
          </div>
        )}
      </div>

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",padding:empty?"0":"24px 0"}}>
        {empty?(
          <div style={{height:"100%",display:"flex",flexDirection:"column",
            alignItems:"center",justifyContent:"center",
            padding:"40px 24px",animation:"fadeIn 0.4s ease"}}>
            <div style={{width:56,height:56,borderRadius:14,background:"#0f172a",
              display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
              <span style={{color:"#f8fafc",fontSize:20,fontWeight:700,fontFamily:"monospace"}}>SR</span>
            </div>
            <h1 style={{fontSize:24,fontWeight:700,color:"#0f172a",
              margin:"0 0 8px 0",letterSpacing:"-0.02em",textAlign:"center"}}>
              Ask anything about CT PURA proceedings
            </h1>
            <p style={{fontSize:14,color:"#64748b",margin:"0 0 36px 0",
              textAlign:"center",maxWidth:480,lineHeight:1.6}}>
              Search across electric utility regulatory filings, orders, briefs, and compliance documents from Connecticut's PURA.
            </p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",
              gap:8,maxWidth:600,width:"100%"}}>
              {EXAMPLES.map((q,i)=>(
                <button key={i} onClick={()=>{setInput(q);inp.current?.focus();}}
                  style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,
                    padding:"12px 14px",textAlign:"left",cursor:"pointer",
                    fontSize:13,color:"#334155",lineHeight:1.5,
                    transition:"all 0.15s ease",fontFamily:"Georgia,serif"}}
                  onMouseEnter={e=>{e.currentTarget.style.background="#f8fafc";e.currentTarget.style.borderColor="#94a3b8"}}
                  onMouseLeave={e=>{e.currentTarget.style.background="#fff";e.currentTarget.style.borderColor="#e2e8f0"}}
                >{q}</button>
              ))}
            </div>
          </div>
        ):(
          <div style={{maxWidth:720,margin:"0 auto",padding:"0 24px"}}>
            {msgs.map((m,i)=><Msg key={i} msg={m}/>)}
            <div ref={bot}/>
          </div>
        )}
      </div>

      {/* Email modal */}
      {showEmail&&(
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",
          display:"flex",alignItems:"center",justifyContent:"center",
          zIndex:50,padding:24,animation:"fadeIn 0.2s ease"}}>
          <div style={{background:"#fff",borderRadius:16,padding:32,
            maxWidth:420,width:"100%",boxShadow:"0 24px 48px rgba(0,0,0,0.15)"}}>
            <div style={{width:44,height:44,borderRadius:10,background:"#0f172a",
              display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
              <span style={{color:"#f8fafc",fontSize:16,fontWeight:700,fontFamily:"monospace"}}>SR</span>
            </div>
            <h2 style={{fontSize:20,fontWeight:700,color:"#0f172a",margin:"0 0 8px 0",letterSpacing:"-0.02em"}}>
              Continue researching
            </h2>
            <p style={{fontSize:14,color:"#64748b",margin:"0 0 24px 0",lineHeight:1.6}}>
              You've used your 5 free queries. Enter your email for unlimited access — no credit card required.
            </p>
            <form onSubmit={submitEmail} style={{display:"flex",flexDirection:"column",gap:12}}>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                placeholder="your@email.com" autoFocus required
                style={{padding:"10px 14px",border:"1px solid #e2e8f0",borderRadius:8,
                  fontSize:14,color:"#0f172a",fontFamily:"Georgia,serif",outline:"none"}}/>
              <button type="submit" style={{background:"#0f172a",color:"#f8fafc",border:"none",
                borderRadius:8,padding:"10px 16px",fontSize:14,fontWeight:600,
                cursor:"pointer",fontFamily:"Georgia,serif"}}>Continue →</button>
            </form>
            <button onClick={()=>setShowEmail(false)} style={{marginTop:12,background:"none",
              border:"none",color:"#94a3b8",fontSize:13,cursor:"pointer",fontFamily:"Georgia,serif"}}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{background:"#fff",borderTop:"1px solid #e2e8f0",
        padding:"16px 24px 20px",flexShrink:0}}>
        <div style={{maxWidth:720,margin:"0 auto"}}>
          {limited&&!emailDone?(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"12px 16px",background:"#fef2f2",border:"1px solid #fecaca",
              borderRadius:10,fontSize:13,color:"#991b1b"}}>
              <span>You've used all 5 free queries for today.</span>
              <button onClick={()=>setShowEmail(true)} style={{background:"#0f172a",color:"#f8fafc",
                border:"none",borderRadius:6,padding:"6px 12px",fontSize:12,
                cursor:"pointer",fontFamily:"Georgia,serif"}}>Continue free →</button>
            </div>
          ):(
            <div style={{display:"flex",gap:10,background:"#f8fafc",
              border:"1px solid #e2e8f0",borderRadius:12,
              padding:"10px 14px",alignItems:"flex-end"}}>
              <textarea ref={inp} value={input}
                onChange={e=>{setInput(e.target.value);e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"}}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send(input)}}}
                placeholder="Ask about any CT PURA electric docket, proceeding, or order..."
                rows={1} disabled={loading||(limited&&!emailDone)}
                style={{flex:1,border:"none",background:"transparent",
                  fontSize:14,color:"#0f172a",lineHeight:1.6,
                  fontFamily:"Georgia,serif",maxHeight:120,overflowY:"auto"}}/>
              <button onClick={()=>send(input)}
                disabled={!input.trim()||loading||(limited&&!emailDone)}
                style={{width:34,height:34,borderRadius:8,
                  background:input.trim()&&!loading&&!(limited&&!emailDone)?"#0f172a":"#e2e8f0",
                  border:"none",
                  cursor:input.trim()&&!loading&&!(limited&&!emailDone)?"pointer":"default",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  flexShrink:0,transition:"background 0.15s ease"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7"
                    stroke={input.trim()&&!loading?"#f8fafc":"#94a3b8"}
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
          <div style={{textAlign:"center",marginTop:8,fontSize:11,color:"#cbd5e1",letterSpacing:"0.01em"}}>
            Connecticut PURA electric dockets · 2020–present · AI answers may contain errors
          </div>
        </div>
      </div>
    </div>
  );
}